import { getNextAutoPaymentDate } from "@taabl/common-lib/calculators/autopay";
import { calculateMinContribution } from "@taabl/common-lib/calculators/contribution";
import { getNearestPeriodFromNow } from "@taabl/common-lib/calculators/period";
import { round } from "@taabl/common-lib/utils";
import axios from "axios";
import { Form, Formik } from "formik";
import React from "react";
import { Button, Card, InputGroup, Modal } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import * as yup from "yup";
import DropZone from "../../components/DropZone";
import FormikCheckbox from "../../components/Forms/FormikCheckbox";
import FormikTextField from "../../components/Forms/FormikTextField";
import LoaderButton from "../../components/LoaderButton";
import PageLoader from "../../components/PageLoader";
import { usePlan } from "../../contexts/PlanContext";
import useAuth from "../../contexts/useAuth";
import { Constants } from "../../libs/Constants";
import { create, get, update } from "../../libs/dbFunctions";
import { calculateBimonthlyPay, formatCurrency } from "../../libs/utils";

const MONTH_PER_YEAR = 12;

const EditContributionButton = () => {
  const { user } = useAuth();
  const { plan, loadPlan } = usePlan();
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [nextPaymentDate, setNextPaymentDate] = React.useState(null);
  const [transferAmount, setTransferAmount] = React.useState(null);
  const [minAmount, setMinAmount] = React.useState(null);
  const [maxAmount, setMaxAmount] = React.useState(null);

  const initialValues = {
    newContributionAmount: undefined,
    oneTimeChange: "",
    files: null,
  };
  const schema = yup.object({
    newContributionAmount: yup
      .number()
      .nullable()
      .required("Contribution Amount is required.")
      .test("newContributionAmount", function (value) {
        if (value < minAmount) {
          return this.createError({
            message: `New Bi-monthly Contribution Amount should be more than $${round(
              minAmount
            )}`,
            path: "newContributionAmount",
          });
        }
        if (
          value &&
          plan?.maxContribution?.percent &&
          maxAmount &&
          value > maxAmount
        ) {
          return this.createError({
            message: `New Bi-monthly Contribution Amount should be less than ${
              plan?.maxContribution?.percent
              }% of Salary. Maximum allowed is $${round(maxAmount)}.`,
            path: "newContributionAmount",
          });
        }
        return true;
      }),
    oneTimeChange: yup.boolean(),
    files: yup.mixed().when("newContributionAmount", {
      is: (newContributionAmount) => newContributionAmount > transferAmount,
      then: (schema) =>
        schema.required(
          "New Pay Stub is needed to increase Contribution Amount."
        ),
    }),
  });

  React.useEffect(() => {
    setLoading(true);
    onLoad();
    setLoading(false);
  }, [user]);

  React.useEffect(() => {
    if (!plan) {
      return;
    }
    // Calculate min and max bimonthly contribution allowed
    setMinAmount(calculateMinAllowedContribution(plan));
    setMaxAmount(calculateAllowedContribution(plan));
  }, [plan]);

  const onLoad = async () => {
    // Calculate the next payment date and amount
    setNextPaymentDate(getNextAutoPaymentDate());
    await loadPlan(user.companyId);
    await loadAutoPayAmount();
  };
  const loadAutoPayAmount = async () => {
    // Load auto pay
    let details = await get(Constants.dbTypes.AUTOPAY, user.id);
    if (details) {
      if (details.oneTimeTransfer) {
        setTransferAmount(details.oneTimeTransfer);
      } else {
        let record = [];
        for (let key in details) {
          if (key.startsWith("pp_")) {
            record.push({
              ...details[key],
            });
          }
        }
        // Even this is not periods object this works as we have dates in it
        let nearestPeriod = getNearestPeriodFromNow(record);
        if (nearestPeriod && nearestPeriod.period) {
          setTransferAmount(nearestPeriod.period.amount);
        }
      }
    }
  };
  const calculateMinAllowedContribution = (plan) => {
    //this method calculates the maximum contribution allowed per purchase period
    let amount = calculateMinContribution(
      plan?.minContribution,
      user.salary,
      plan?.purchasePeriod
    );
    // Bimonthly min contribution amount
    return calculateBimonthlyPay(amount, plan.purchasePeriod);
  };
  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    if (values.files && values.files.length > 0) {
      await createApprovalRequest(values);
    } else {
      await updateContributionAmount(values);
      // Reload Auto Payobject
      loadAutoPayAmount();
    }

    setSubmitting(false);
  };
  const createApprovalRequest = async (values) => {
    let saveFields = {
      userId: user.id,
      contributionAmount: Number(values.newContributionAmount),
      payStubFile: values.files[0].name,
      fileType: values.files[0].type,
      requestType: "contribution",
      activeCompanyId: user.companyId,
    };
    if (values?.oneTimeChange) {
      saveFields.oneTime = values.oneTimeChange;
    }
    //verify the account
    try {
      const response = await create(
        Constants.dbTypes.APPROVAL_REQUEST,
        saveFields
      );
      if (response && response.url && values.files && values.files.length > 0) {
        // Upload the file
        await uploadFile(response, values.files[0]);
      }
      toast.success(
        "Contribution amount increase request is submitted for approval."
      );
      setOpen(false);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };
  const uploadFile = async (response, file) => {
    // Upload file to s3
    const options = {
      params: {
        Key: response.key,
        ContentType: file.type,
      },
      headers: {
        "Content-Type": file.type,
      },
    };
    try {
      await axios.put(response.url, file, options);
    } catch (err) {
      console.log(err);
      toast.error("Unable to upload file. Please contract your administrator");
    }
  };
  const updateContributionAmount = async (values) => {
    let saveFields = {
      userId: user.id,
      amount: Number(values.newContributionAmount),
      activeCompanyId: user.companyId,
    };
    if (values?.oneTimeChange) {
      saveFields.oneTime = values.oneTimeChange;
    }
    //verify the account
    try {
      await update(Constants.dbTypes.AUTOPAY, saveFields);
      toast.success("Contribution Amount update request is submitted.");
      setOpen(false);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };
  const calculateAllowedContribution = (plan) => {
    if (!user.salary || !plan.maxContribution) {
      return null;
    }
    let annualContribution =
      (user.salary * plan?.maxContribution?.percent) / 100;
    return annualContribution / (2 * MONTH_PER_YEAR);
  };
  return (
    <PageLoader isActive={loading}>
      <Button variant="link" onClick={() => setOpen(true)}>
        Edit
      </Button>
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="lg"
        backdrop="static"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="contribution-modal font-secondary"
      >
        <Modal.Header className="p-4" closeButton>
          <Modal.Title>Update Contribution Amount</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span className="mb-4 form-label font-primary">
            Your next bi-monthly contribution is {" "}
            {formatCurrency(transferAmount ? transferAmount : 0)} on{" "}
            {nextPaymentDate}.
          </span>
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validateOnMount
            validateOnChange
          >
            {({
              isValid,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              values,
            }) => (
                <Form className="w-100 pt-4">
                  <FormikTextField
                    size="lg"
                    controlId="newContributionAmount"
                    label="New Bi-Monthly Contribution Amount"
                    type="number"
                    name="newContributionAmount"
                    validateOnKeyChange={true}
                    className="mb-4"
                    inputGroupPrepend={
                      <InputGroup.Text id="basic-addon2">$</InputGroup.Text>
                    }
                  />
                  {values.newContributionAmount &&
                    values.newContributionAmount > transferAmount && (
                      <>
                        <span className="mb-4 form-label font-primary">
                          Please upload your new pay stub to increase the
                          contribution amount
                      </span>
                        <div className="d-flex justify-content-center drag-drop-card-pay-stub">
                          <Card>
                            {!values.files && (
                              <DropZone
                                onSubmit={(selectedFiles) => {
                                  setFieldValue("files", selectedFiles, true);
                                }}
                                errorMessage={"Drop to Upload Pay Stub"}
                                id="company"
                                uploading={false}
                              />
                            )}
                            {values.files?.length > 0 && (
                              <div className="d-flex justify-content-center align-items-center h-100">
                                <div className="font-size-4">
                                  {values.files[0].name}
                                </div>
                                <div className="ms-1">
                                  <MdDelete
                                    className="text-danger"
                                    onClick={() => {
                                      setFieldValue("files", null, true);
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </Card>
                        </div>
                      </>
                    )}
                  <FormikCheckbox
                    controlId="oneTimeChange"
                    name="oneTimeChange"
                    md="6"
                    sm="12"
                    className="my-2"
                    label={
                      <span className="form-label font-weight-normal">
                        One time change of contribution
                    </span>
                    }
                  />
                  <LoaderButton
                    block
                    type="submit"
                    variant="primary"
                    size="lg"
                    text="Next"
                    className="mb-2"
                    isLoading={isSubmitting}
                    disabled={!isValid || isSubmitting}
                  >
                    Update
                </LoaderButton>
                </Form>
              )}
          </Formik>
        </Modal.Body>
      </Modal>
    </PageLoader>
  );
};

export default EditContributionButton;